import React, { useState, useMemo } from "react";
import { useReactFlow } from "@xyflow/react";
import classNames from "classnames";
import { WorkflowStep, WorkflowServiceAction, ActionContextType } from "../../../__generated__/api";
import ActionsTab from "./tabs/ActionsTab";
import IntegrationsTab from "./tabs/IntegrationsTab";
import ActionConfiguration from "./tabs/ActionConfiguration";
import { useWorkflow, WorkflowStepGroupWithNodeData } from "../providers/WorkflowProvider";
import TabStyles from "../../theme/settings/Settings.module.scss";
import Button from "../../theme/button/Button";
import { CardServiceActions } from "../../workflow/WorkflowCardCreate";
import createId from "../../../create-id";

enum TabType {
    Actions = "actions",
    Integrations = "integrations",
}

const TABS = [
    {
        key: TabType.Actions,
        text: "Actions",
    },
    {
        key: TabType.Integrations,
        text: "Integrations",
    },
];

export default function ActionsSelector() {
    const {
        selectedNode, serviceConfiguration, selectedService, setServiceConfiguration, currentStep, setSelectedService,
        workflow, actionsContextType,
    } = useWorkflow();
    const [current, setCurrent] = useState<TabType>(TabType.Actions);

    const { getNodes } = useReactFlow();
    const nodesList = useMemo(() => getNodes(), [getNodes]);

    const filteredTabs = useMemo(() => {
        if (actionsContextType === ActionContextType.Request) {
            return TABS.filter(tab => tab.key !== TabType.Integrations);
        }

        return TABS;
    }, [actionsContextType]);

    const buttons = useMemo(() => filteredTabs.map(tab => (
        <Button
            key={tab.key}
            variant="ghost-gray"
            size="small"
            className={current === tab.key ? TabStyles["button-hover"] : ""}
            onClick={() => setCurrent(tab.key as TabType)}
        >
            {tab.text}
        </Button>
    )), [filteredTabs, current]);

    const parentInfo = useMemo(() => {
        let ancestorId: string | null;
        let parentGroup: WorkflowStepGroupWithNodeData | undefined;

        if (currentStep) {
            ancestorId = currentStep.id;
            parentGroup = currentStep;
        } else {
            const parentId = selectedNode?.parentId;

            if (!parentId) {
                return { parentGroup: undefined, parentStep: undefined };
            }

            const parentGroupNode = nodesList.find(node => node.id === parentId);

            if (!parentGroupNode) {
                return { parentGroup: undefined, parentStep: undefined };
            }

            parentGroup = parentGroupNode.data as WorkflowStepGroupWithNodeData;
            ancestorId = parentGroup.parentId;
        }

        const nodesMap = nodesList.toIdMap("id");

        function findWorkflowStepNode(): WorkflowStep | undefined {
            do {
                const ancestorNode = nodesMap[ancestorId as string];

                if (!ancestorNode) {
                    return undefined;
                }

                if (ancestorNode.data?.__typename === "WorkflowStep") {
                    return ancestorNode.data as WorkflowStep;
                }
                ancestorId = (ancestorNode.data as WorkflowStepGroupWithNodeData).parentId;
            } while (ancestorId);

            return undefined;
        }

        const parentStep = findWorkflowStepNode();

        return { parentGroup, parentStep };
    }, [nodesList, selectedNode, currentStep]);

    const { parentGroup, parentStep } = parentInfo;
    if (selectedNode && selectedNode.action) {
        return (
            <ActionConfiguration
                actionId={selectedNode.id}
                service={selectedNode.action}
                parentGroup={parentGroup}
                parentStep={parentStep}
            />
        );
    }

    if (serviceConfiguration) {
        const actionId = `urn:fq:workflow-step:(${workflow?.id},${createId()})`;
        return (
            <ActionConfiguration
                actionId={actionId}
                service={serviceConfiguration}
                parentGroup={parentGroup}
                parentStep={parentStep}
            />
        );
    }

    if (selectedService) {
        return (
            <CardServiceActions
                params={{
                    service: selectedService,
                    serviceId: selectedService.id,
                }}
                onSelected={(action: WorkflowServiceAction) => {
                    setServiceConfiguration(action);
                }}
                onHide={() => {
                    setSelectedService(null);
                }}
            />
        );
    }

    return (
        <div>
            <div className={TabStyles["button-list-container"]}>
                <div className={classNames(
                    TabStyles["button-list"],
                    TabStyles["button-list-show"],
                    TabStyles["navigation-tabs"],
                )}
                >
                    {buttons}
                </div>
            </div>
            {current === TabType.Actions && <ActionsTab />}
            {current === TabType.Integrations && <IntegrationsTab />}
        </div>
    );
}
